import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { InvoiceGenerator } from './InvoiceGenerator';
import { BlogManager } from './BlogManager';
import { 
  Star, 
  Check, 
  X, 
  Trash2, 
  Eye, 
  Calendar, 
  Building, 
  User,
  AlertTriangle,
  RefreshCw,
  Package,
  CheckCircle,
  Clock,
  Mail,
  Download
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPanel = () => {
  const [reviews, setReviews] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('reviews'); // reviews, quotes, orders, calculator, invoice
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [quoteFilter, setQuoteFilter] = useState('all'); // all, pending, sent, accepted
  const [orderFilter, setOrderFilter] = useState('all'); // all, confirmed, assigned, transit, delivered
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // Search for quote/tracking ID
  
  // Send Quote Modal State
  const [sendQuoteModal, setSendQuoteModal] = useState(null); // Stores quote being sent
  const [quotePrice, setQuotePrice] = useState('');
  const [quoteMessage, setQuoteMessage] = useState('');
  
  // Price Calculator State
  const [calcDistance, setCalcDistance] = useState('');
  const [calcVehicle, setCalcVehicle] = useState('Small Van');
  const [calcDeliveryType, setCalcDeliveryType] = useState('same-day'); // same-day or urgent
  const [calcManualPrice, setCalcManualPrice] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExportData = async () => {
    try {
      setExporting(true);
      const res = await axios.get(`${API}/admin/export`);
      const data = res.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `ais-express-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const counts = data.counts || {};
      alert(
        `✅ Backup downloaded!\n\n` +
        `Orders/Quotes: ${counts.orders || 0}\n` +
        `Reviews: ${counts.reviews || 0}\n` +
        `Contact messages: ${counts.contact_submissions || 0}\n` +
        `Blog posts: ${counts.blog_posts || 0}\n` +
        `Chat messages: ${counts.chat_messages || 0}\n\n` +
        `💡 Tip: Save this file to Google Drive or email it to yourself for safekeeping.`
      );
    } catch (e) {
      console.error('Export failed:', e);
      alert('Export failed. Please try again or check your connection.');
    } finally {
      setExporting(false);
    }
  };

  // Simple password authentication
  const handleLogin = () => {
    if (password === 'aisexpress2024') { // You can change this password
      setIsAuthenticated(true);
      fetchReviews();
      fetchOrders();
    } else {
      alert('Incorrect password');
    }
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      alert('Error loading reviews');
    } finally {
      setLoading(false);
    }
  };

  const updateReviewStatus = async (reviewId, status) => {
    try {
      setActionLoading(reviewId);
      const response = await axios.put(`${API}/admin/reviews/${reviewId}/status`, {
        status: status
      });
      
      if (response.data.success) {
        await fetchReviews(); // Refresh the list
        alert(`Review ${status} successfully!`);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error updating review:', error);
      alert('Error updating review');
    } finally {
      setActionLoading(null);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(reviewId);
      const response = await axios.delete(`${API}/admin/reviews/${reviewId}`);
      
      if (response.data.success) {
        await fetchReviews(); // Refresh the list
        alert('Review deleted successfully!');
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error deleting review:', error);
      alert('Error deleting review');
    } finally {
      setActionLoading(null);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredReviews = reviews.filter(review => {
    if (filter === 'all') return true;
    return review.status === filter;
  });

  const getStatusCounts = () => {
    return {
      all: reviews.length,
      pending: reviews.filter(r => r.status === 'pending').length,
      approved: reviews.filter(r => r.status === 'approved').length,
      rejected: reviews.filter(r => r.status === 'rejected').length,
    };
  };

  // Order Management Functions
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(Array.isArray(response.data) ? response.data : (response.data.orders || []));
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert('Error loading orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status, driverData = {}) => {
    try {
      setActionLoading(orderId);
      const updateData = { status, ...driverData };
      
      const response = await axios.put(`${API}/admin/orders/${orderId}`, updateData);
      
      if (response.data.success) {
        await fetchOrders(); // Refresh the list
        alert(`Order updated successfully!`);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Error updating order');
    } finally {
      setActionLoading(null);
    }
  };

  const deleteOrder = async (orderId, trackingId) => {
    if (!window.confirm(`Are you sure you want to delete order ${trackingId || orderId}? This action cannot be undone.`)) {
      return;
    }

    try {
      setActionLoading(orderId);
      const response = await axios.delete(`${API}/admin/orders/${orderId}`);
      
      if (response.data.success) {
        await fetchOrders(); // Refresh the list
        alert('Order deleted successfully!');
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Error deleting order');
    } finally {
      setActionLoading(null);
    }
  };

  const openSendQuoteModal = (quote) => {
    setSendQuoteModal(quote);
    setQuotePrice('');
    setQuoteMessage('Thank you for your quote request. Based on your requirements, we are pleased to offer the following price for your delivery.');
  };

  const closeSendQuoteModal = () => {
    setSendQuoteModal(null);
    setQuotePrice('');
    setQuoteMessage('');
  };

  const sendQuoteToCustomer = async () => {
    if (!quotePrice || parseFloat(quotePrice) <= 0) {
      alert('Please enter a valid price');
      return;
    }

    if (!quoteMessage.trim()) {
      alert('Please enter a message');
      return;
    }

    try {
      setActionLoading(sendQuoteModal.id);

      const quoteData = {
        quote_id: sendQuoteModal.quote_id,
        customer_name: sendQuoteModal.name,
        customer_email: sendQuoteModal.email,
        customer_phone: sendQuoteModal.phone,
        pickup_address: sendQuoteModal.pickup_address,
        delivery_address: sendQuoteModal.delivery_address,
        delivery_date: sendQuoteModal.delivery_date,
        vehicle_type: sendQuoteModal.vehicle_type,
        package_details: sendQuoteModal.package_details,
        price: parseFloat(quotePrice),
        message: quoteMessage
      };

      const response = await axios.post(`${API}/admin/send-quote`, quoteData);

      if (response.data.success) {
        alert(`Quote sent successfully to ${sendQuoteModal.email}!`);
        closeSendQuoteModal();
        await fetchOrders(); // Refresh the list
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error sending quote:', error);
      alert('Error sending quote. Please check the details and try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const addDeliveryPhoto = async (orderId, photoFile) => {
    try {
      setActionLoading(`photo_${orderId}`);
      
      // Convert file to base64
      const base64Photo = await convertFileToBase64(photoFile);
      
      const response = await axios.post(`${API}/admin/orders/${orderId}/photos`, {
        photo_url: base64Photo
      });
      
      if (response.data.success) {
        await fetchOrders(); // Refresh the list
        alert('Photo added successfully!');
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error adding photo:', error);
      alert('Error adding photo');
    } finally {
      setActionLoading(null);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handlePhotoUpload = (orderId, event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size must be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      addDeliveryPhoto(orderId, file);
    }
  };

  const handleStatusChange = (orderId, newStatus, currentStatus) => {
    if (newStatus === currentStatus) return;
    
    const confirmMessage = `Are you sure you want to change the status to "${newStatus}"?`;
    
    if (window.confirm(confirmMessage)) {
      updateOrderStatus(orderId, newStatus);
    } else {
      // Reset the dropdown to current status
      const dropdown = document.querySelector(`select[data-order-id="${orderId}"]`);
      if (dropdown) {
        dropdown.value = currentStatus;
      }
    }
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'Delivered':
        return 'bg-green-100 text-green-800';
      case 'In Transit':
      case 'Out for Delivery':
        return 'bg-blue-100 text-blue-800';
      case 'Package Collected':
        return 'bg-orange-100 text-orange-800';
      case 'Driver Assigned':
        return 'bg-purple-100 text-purple-800';
      case 'Booking Confirmed':
        return 'bg-yellow-100 text-yellow-800';
      case 'Order Received':
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Quote Management Functions
  const handleApproveQuote = async (orderId, quoteId) => {
    try {
      setActionLoading(orderId);
      const response = await axios.put(`${API}/admin/quotes/${quoteId}/approve`);
      
      if (response.data.success) {
        await fetchOrders(); // Refresh the list
        alert(response.data.message);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error approving quote:', error);
      alert('Error approving quote');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmBooking = async (orderId, quoteId) => {
    if (!window.confirm('This will generate a tracking ID and send confirmation email to the customer. Continue?')) {
      return;
    }

    try {
      setActionLoading(orderId);
      const response = await axios.put(`${API}/admin/quotes/${quoteId}/confirm`);
      
      if (response.data.success) {
        await fetchOrders(); // Refresh the list
        alert(response.data.message);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error confirming booking:', error);
      alert('Error confirming booking');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectQuote = async (orderId) => {
    if (!window.confirm('Are you sure you want to reject this quote? You can undo this later if needed.')) {
      return;
    }

    try {
      setActionLoading(orderId);
      const response = await axios.put(`${API}/admin/orders/${orderId}`, {
        status: 'Quote Rejected'
      });
      
      if (response.data.success) {
        await fetchOrders(); // Refresh the list
        alert('Quote rejected. You can undo this action if it was a mistake.');
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error rejecting quote:', error);
      alert('Error rejecting quote');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUndoReject = async (orderId) => {
    if (!window.confirm('Restore this quote back to pending status?')) {
      return;
    }

    try {
      setActionLoading(orderId);
      const response = await axios.put(`${API}/admin/orders/${orderId}`, {
        status: 'Quote Pending'
      });
      
      if (response.data.success) {
        await fetchOrders(); // Refresh the list
        alert('Quote restored to pending status successfully!');
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error restoring quote:', error);
      alert('Error restoring quote');
    } finally {
      setActionLoading(null);
    }
  };

  // Separate quotes from orders based on status
  const quotes = orders.filter(order => 
    order.status === 'Quote Pending' || 
    order.status === 'Quote Sent' ||
    order.status === 'Quote Rejected'
  );
  
  const confirmedOrders = orders.filter(order => 
    order.status !== 'Quote Pending' && 
    order.status !== 'Quote Sent' &&
    order.status !== 'Quote Rejected'
  );

  // Filter quotes based on quoteFilter and search
  const filteredQuotes = quotes.filter(order => {
    // First apply status filter
    let statusMatch = true;
    if (quoteFilter !== 'all') {
      if (quoteFilter === 'pending') {
        statusMatch = order.status === 'Quote Pending';
      } else if (quoteFilter === 'sent') {
        statusMatch = order.status === 'Quote Sent';
      } else if (quoteFilter === 'accepted') {
        statusMatch = order.status === 'Quote Accepted';
      }
    }
    
    // Then apply search query filter
    let searchMatch = true;
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      searchMatch = 
        (order.quote_id && order.quote_id.toLowerCase().includes(query)) ||
        order.customer_name.toLowerCase().includes(query) ||
        order.email.toLowerCase().includes(query) ||
        order.phone.includes(query);
    }
    
    return statusMatch && searchMatch;
  });

  // Filter confirmed orders based on orderFilter and search
  const filteredOrders = confirmedOrders.filter(order => {
    // First apply status filter
    let statusMatch = true;
    if (orderFilter !== 'all') {
      statusMatch = order.status.toLowerCase().includes(orderFilter.toLowerCase());
    }
    
    // Then apply search query filter
    let searchMatch = true;
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      searchMatch = 
        (order.quote_id && order.quote_id.toLowerCase().includes(query)) ||
        (order.tracking_id && order.tracking_id.toLowerCase().includes(query)) ||
        order.customer_name.toLowerCase().includes(query) ||
        order.email.toLowerCase().includes(query) ||
        order.phone.includes(query);
    }
    
    return statusMatch && searchMatch;
  });

  const getQuoteCounts = () => {
    return {
      all: quotes.length,
      pending: quotes.filter(o => o.status === 'Quote Pending').length,
      sent: quotes.filter(o => o.status === 'Quote Sent').length,
      rejected: quotes.filter(o => o.status === 'Quote Rejected').length,
    };
  };

  const getOrderCounts = () => {
    return {
      all: confirmedOrders.length,
      confirmed: confirmedOrders.filter(o => o.status === 'Booking Confirmed').length,
      assigned: confirmedOrders.filter(o => o.status === 'Driver Assigned').length,
      transit: confirmedOrders.filter(o => o.status.includes('Transit') || o.status.includes('Delivery')).length,
      delivered: confirmedOrders.filter(o => o.status === 'Delivered').length,
    };
  };

  const getOrderProgressSteps = (order) => {
    const allSteps = [
      'Order Received',
      'Booking Confirmed',
      'Driver Assigned', 
      'Package Collected',
      'In Transit',
      'Out for Delivery',
      'Delivered'
    ];
    
    // Find current step index, defaulting to first step if status not found
    let currentStepIndex = allSteps.indexOf(order.status);
    if (currentStepIndex === -1) {
      // If status is 'Booking Confirmed', it's actually step 1 (second step)
      if (order.status === 'Booking Confirmed') {
        currentStepIndex = 1;
      } else {
        // For any other status, find it in the array or default to 0
        currentStepIndex = allSteps.findIndex(step => step === order.status);
        if (currentStepIndex === -1) currentStepIndex = 0;
      }
    }
    
    return allSteps.map((step, index) => ({
      name: step,
      completed: index <= currentStepIndex,
      current: index === currentStepIndex,
      next: index === currentStepIndex + 1
    }));
  };

  const getStepColor = (step) => {
    if (step.completed) {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (step.next) {
      return 'bg-orange-100 text-orange-800 border-orange-200 animate-pulse';
    } else {
      return 'bg-gray-100 text-gray-500 border-gray-200';
    }
  };

  // Price Calculator Data and Functions
  const pricingData = {
    'same-day': {
      'Small Van': { range: '20-80', perMile: 1.45 },
      'SWB': { range: '30-90', perMile: 1.75 },
      'MWB': { range: '40-110', perMile: 1.85 },
      'XLWB': { range: '40-120', perMile: 2.00 },
      'Luton': { range: '60-140', perMile: 2.30 }
    },
    'urgent': {
      'Small Van': { range: '40-100', perMile: 2.00 },
      'SWB': { range: '50-110', perMile: 2.20 },
      'MWB': { range: '60-120', perMile: 2.30 },
      'XLWB': { range: '70-120', perMile: 2.50 },
      'Luton': { range: '70-160', perMile: 2.80 }
    }
  };

  const calculatePrice = () => {
    const distance = parseFloat(calcDistance);
    
    if (!distance || distance <= 0) {
      alert('Please enter a valid distance');
      return;
    }

    if (distance > 500) {
      alert('Distance cannot exceed 500 miles');
      return;
    }

    const pricing = pricingData[calcDeliveryType][calcVehicle];
    
    if (distance <= 60) {
      // For 0-60 miles: Show price range, admin must manually select
      if (!calcManualPrice || parseFloat(calcManualPrice) <= 0) {
        alert(`For ${distance} miles, price range is £${pricing.range}. Please enter a price within this range.`);
        return;
      }
      
      const [min, max] = pricing.range.split('-').map(Number);
      const manualPrice = parseFloat(calcManualPrice);
      
      if (manualPrice < min || manualPrice > max) {
        alert(`Price must be between £${min} and £${max} for this distance and vehicle type.`);
        return;
      }
      
      setCalculatedPrice({
        distance,
        vehicle: calcVehicle,
        deliveryType: calcDeliveryType === 'same-day' ? 'Same-day/Next-day (1h-12h)' : 'Urgent (0h-6h)',
        priceRange: pricing.range,
        finalPrice: manualPrice,
        calculation: `Manual selection within range £${pricing.range}`
      });
    } else {
      // For 60-500 miles: Auto-calculate using per-mile rate (total distance × rate)
      const calculatedPrice = (distance * pricing.perMile).toFixed(2);
      
      setCalculatedPrice({
        distance,
        vehicle: calcVehicle,
        deliveryType: calcDeliveryType === 'same-day' ? 'Same-day/Next-day (1h-12h)' : 'Urgent (0h-6h)',
        perMileRate: pricing.perMile,
        finalPrice: parseFloat(calculatedPrice),
        calculation: `${distance} miles × £${pricing.perMile} = £${calculatedPrice}`
      });
      
      setCalcManualPrice(''); // Clear manual price field for auto-calculated prices
    }
  };

  const resetCalculator = () => {
    setCalcDistance('');
    setCalcVehicle('Small Van');
    setCalcDeliveryType('same-day');
    setCalcManualPrice('');
    setCalculatedPrice(null);
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 shadow-xl">
          <CardContent className="p-0 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 mx-auto mb-6">
              <Building className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              AIS Express Admin
            </h1>
            <p className="text-slate-600 mb-6">
              Enter password to access review management
            </p>
            
            <div className="space-y-4">
              <Input
                type="password"
                placeholder="Admin Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full px-4 py-3 text-center"
              />
              <Button 
                onClick={handleLogin}
                className="w-full bg-emerald-600 hover:bg-emerald-700 py-3"
              >
                Login to Admin Panel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 p-4">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <Card className="mb-6 shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 flex items-center space-x-3">
                  <Building className="h-8 w-8 text-emerald-600" />
                  <span>AIS Express Admin Panel</span>
                </h1>
                <p className="text-slate-600 mt-1">
                  {activeTab === 'reviews' ? 'Manage customer reviews and feedback' : 'Manage delivery orders and status updates'}
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  onClick={handleExportData}
                  disabled={exporting}
                  variant="outline"
                  className="flex items-center space-x-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="admin-export-btn"
                  title="Download a backup of all orders, quotes, reviews, blog posts as JSON"
                >
                  <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
                  <span>{exporting ? 'Exporting...' : 'Export All Data'}</span>
                </Button>
                <Button 
                  onClick={activeTab === 'reviews' ? fetchReviews : fetchOrders}
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700 flex items-center space-x-2"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Navigation Tabs */}
        <Card className="mb-6 shadow-lg">
          <CardContent className="p-6">
            <div className="flex space-x-4 mb-6">
              <Button
                variant={activeTab === 'reviews' ? "default" : "outline"}
                onClick={() => setActiveTab('reviews')}
                className={`${
                  activeTab === 'reviews' 
                    ? 'bg-emerald-600 hover:bg-emerald-700' 
                    : 'hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300'
                }`}
              >
                Reviews ({reviews.length})
              </Button>
              <Button
                variant={activeTab === 'quotes' ? "default" : "outline"}
                onClick={() => setActiveTab('quotes')}
                className={`${
                  activeTab === 'quotes' 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300'
                }`}
              >
                Quotes ({quotes.length})
              </Button>
              <Button
                variant={activeTab === 'orders' ? "default" : "outline"}
                onClick={() => setActiveTab('orders')}
                className={`${
                  activeTab === 'orders' 
                    ? 'bg-emerald-600 hover:bg-emerald-700' 
                    : 'hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300'
                }`}
              >
                Orders ({confirmedOrders.length})
              </Button>
              <Button
                variant={activeTab === 'calculator' ? "default" : "outline"}
                onClick={() => setActiveTab('calculator')}
                className={`${
                  activeTab === 'calculator' 
                    ? 'bg-purple-600 hover:bg-purple-700' 
                    : 'hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300'
                }`}
              >
                Price Calculator
              </Button>
              <Button
                variant={activeTab === 'invoice' ? "default" : "outline"}
                onClick={() => setActiveTab('invoice')}
                className={`${
                  activeTab === 'invoice' 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300'
                }`}
              >
                Invoice Generator
              </Button>
              <Button
                variant={activeTab === 'blog' ? "default" : "outline"}
                onClick={() => setActiveTab('blog')}
                className={`${
                  activeTab === 'blog' 
                    ? 'bg-pink-600 hover:bg-pink-700' 
                    : 'hover:bg-pink-50 hover:text-pink-700 hover:border-pink-300'
                }`}
                data-testid="admin-tab-blog"
              >
                Blog
              </Button>
            </div>

            {/* Status Filter Tabs */}
            <div className="flex flex-wrap gap-2">
              {activeTab === 'reviews' ? (
                Object.entries(getStatusCounts()).map(([status, count]) => (
                  <Button
                    key={status}
                    variant={filter === status ? "default" : "outline"}
                    onClick={() => setFilter(status)}
                    className={`capitalize ${
                      filter === status 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300'
                    }`}
                  >
                    {status} ({count})
                  </Button>
                ))
              ) : activeTab === 'quotes' ? (
                Object.entries(getQuoteCounts()).map(([status, count]) => (
                  <Button
                    key={status}
                    variant={quoteFilter === status ? "default" : "outline"}
                    onClick={() => setQuoteFilter(status)}
                    className={`capitalize ${
                      quoteFilter === status 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300'
                    }`}
                  >
                    {status} ({count})
                  </Button>
                ))
              ) : (
                Object.entries(getOrderCounts()).map(([status, count]) => (
                  <Button
                    key={status}
                    variant={orderFilter === status ? "default" : "outline"}
                    onClick={() => setOrderFilter(status)}
                    className={`capitalize ${
                      orderFilter === status 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300'
                    }`}
                  >
                    {status} ({count})
                  </Button>
                ))
              )}
            </div>
            
            {/* Search Bar for Quotes and Orders Tab */}
            {(activeTab === 'quotes' || activeTab === 'orders') && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder={activeTab === 'quotes' 
                      ? "Search by Quote ID, Name, Email, or Phone..." 
                      : "Search by Quote ID, Tracking ID, Name, Email, or Phone..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border-2 border-slate-200 focus:border-emerald-500 rounded-lg"
                  />
                  <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <p className="text-sm text-slate-600 mt-2">
                    Found {activeTab === 'quotes' ? filteredQuotes.length : filteredOrders.length} result{(activeTab === 'quotes' ? filteredQuotes.length : filteredOrders.length) !== 1 ? 's' : ''} for "{searchQuery}"
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Content Based on Active Tab */}
        {activeTab === 'reviews' ? (
          <>
            {/* Reviews List */}
            {loading ? (
              <Card className="shadow-lg">
                <CardContent className="p-12 text-center">
                  <RefreshCw className="h-8 w-8 text-emerald-600 animate-spin mx-auto mb-4" />
                  <p className="text-slate-600">Loading reviews...</p>
                </CardContent>
              </Card>
            ) : filteredReviews.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="p-12 text-center">
              <Eye className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Reviews Found</h3>
              <p className="text-slate-600">
                {filter === 'all' 
                  ? 'No reviews have been submitted yet.' 
                  : `No ${filter} reviews found.`
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredReviews.map((review) => (
              <Card key={review.id} className="shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row justify-between space-y-4 lg:space-y-0">
                    {/* Review Content */}
                    <div className="flex-1 lg:pr-6">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <Badge className={`px-3 py-1 ${getStatusColor(review.status)}`}>
                          {review.status.toUpperCase()}
                        </Badge>
                        {renderStars(review.rating)}
                        <div className="flex items-center text-sm text-slate-500">
                          <Calendar className="h-4 w-4 mr-1" />
                          {review.created_at}
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <div className="flex items-center space-x-4 text-sm text-slate-700 mb-2">
                          <div className="flex items-center">
                            <User className="h-4 w-4 mr-1" />
                            <span className="font-medium">{review.name}</span>
                          </div>
                          {review.company && (
                            <div className="flex items-center">
                              <Building className="h-4 w-4 mr-1" />
                              <span>{review.company}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-slate-800 leading-relaxed">"{review.text}"</p>
                        </div>
                        
                        {review.approved_at && (
                          <div className="text-xs text-green-600 mt-2">
                            Approved: {review.approved_at}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex lg:flex-col gap-2 lg:min-w-[140px]">
                      {review.status === 'pending' && (
                        <>
                          <Button
                            onClick={() => updateReviewStatus(review.id, 'approved')}
                            disabled={actionLoading === review.id}
                            className="bg-green-600 hover:bg-green-700 text-white flex items-center space-x-1 flex-1 lg:flex-none"
                          >
                            <Check className="h-4 w-4" />
                            <span>Approve</span>
                          </Button>
                          <Button
                            onClick={() => updateReviewStatus(review.id, 'rejected')}
                            disabled={actionLoading === review.id}
                            variant="destructive"
                            className="flex items-center space-x-1 flex-1 lg:flex-none"
                          >
                            <X className="h-4 w-4" />
                            <span>Reject</span>
                          </Button>
                        </>
                      )}
                      
                      {review.status === 'approved' && (
                        <Button
                          onClick={() => updateReviewStatus(review.id, 'pending')}
                          disabled={actionLoading === review.id}
                          variant="outline"
                          className="flex items-center space-x-1 flex-1 lg:flex-none"
                        >
                          <AlertTriangle className="h-4 w-4" />
                          <span>Unpublish</span>
                        </Button>
                      )}
                      
                      {review.status === 'rejected' && (
                        <Button
                          onClick={() => updateReviewStatus(review.id, 'approved')}
                          disabled={actionLoading === review.id}
                          className="bg-green-600 hover:bg-green-700 flex items-center space-x-1 flex-1 lg:flex-none"
                        >
                          <Check className="h-4 w-4" />
                          <span>Approve</span>
                        </Button>
                      )}
                      
                      <Button
                        onClick={() => deleteReview(review.id)}
                        disabled={actionLoading === review.id}
                        variant="destructive"
                        className="flex items-center space-x-1 flex-1 lg:flex-none"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Delete</span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </>
        ) : activeTab === 'quotes' ? (
          <>
            {/* Quotes List */}
            {loading ? (
              <Card className="shadow-lg">
                <CardContent className="p-12 text-center">
                  <RefreshCw className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-4" />
                  <p className="text-slate-600">Loading quotes...</p>
                </CardContent>
              </Card>
            ) : filteredQuotes.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="p-12 text-center">
                  <Package className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Quotes Found</h3>
                  <p className="text-slate-600">
                    {quoteFilter === 'all' 
                      ? 'No quote requests have been received yet.' 
                      : `No ${quoteFilter} quotes found.`
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredQuotes.map((order) => (
                  <Card key={order.id} className="shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row justify-between space-y-4 lg:space-y-0 lg:space-x-6">
                        {/* Quote Info */}
                        <div className="flex-1">
                          {/* Quote Reference Section - Prominent Display */}
                          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0">
                              <div className="flex-1">
                                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Quote Reference</p>
                                <h3 className="text-2xl font-bold text-blue-900 font-mono tracking-wider">
                                  {order.quote_id}
                                </h3>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row justify-between items-start mb-4">
                            <div>
                              <div className="flex items-center space-x-2 mb-1">
                                <User className="h-4 w-4 text-slate-500" />
                                <span className="text-lg font-semibold text-slate-900">{order.customer_name}</span>
                                {order.company && (
                                  <span className="text-slate-500">• {order.company}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Badge className={getOrderStatusColor(order.status)}>
                                {order.status}
                              </Badge>
                              {order.urgent && (
                                <Badge className="bg-red-100 text-red-800">
                                  ⚡ URGENT
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                            <div>
                              <p className="font-medium text-slate-900">Contact:</p>
                              <p className="text-slate-600">{order.phone}</p>
                              <p className="text-slate-600">{order.email}</p>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">Vehicle Type:</p>
                              <p className="text-slate-600">{order.vehicle_type}</p>
                            </div>
                          </div>
                          
                          {/* Pickup and Delivery Section */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            {/* Pickup Details */}
                            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                              <h4 className="font-semibold text-orange-900 mb-2 flex items-center">
                                📍 Pickup Details
                              </h4>
                              <div className="space-y-1 text-sm">
                                <p className="text-slate-700"><strong>Address:</strong></p>
                                <p className="text-slate-600">{order.pickup_address}</p>
                                <p className="text-slate-600"><strong>Postcode:</strong> {order.pickup_postcode}</p>
                                <p className="text-slate-600"><strong>Date:</strong> {order.pickup_date}</p>
                                <p className="text-slate-600"><strong>Time:</strong> {order.pickup_time}</p>
                              </div>
                            </div>
                            
                            {/* Delivery Details */}
                            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                              <h4 className="font-semibold text-emerald-900 mb-2 flex items-center">
                                🎯 Delivery Details
                              </h4>
                              <div className="space-y-1 text-sm">
                                <p className="text-slate-700"><strong>Address:</strong></p>
                                <p className="text-slate-600">{order.delivery_address}</p>
                                <p className="text-slate-600"><strong>Postcode:</strong> {order.delivery_postcode}</p>
                                {order.delivery_date && (
                                  <p className="text-slate-600"><strong>Requested Date:</strong> {order.delivery_date}</p>
                                )}
                                {order.delivery_time && (
                                  <p className="text-slate-600"><strong>Est. Time:</strong> {order.delivery_time}</p>
                                )}
                                {!order.delivery_date && (
                                  <p className="text-emerald-700 font-medium">✓ Same-day delivery</p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Package Information */}
                          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                            <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
                              📦 Package Information
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div>
                                <p className="text-slate-700"><strong>Description:</strong></p>
                                <p className="text-slate-600">{order.package_description}</p>
                              </div>
                              {order.special_instructions && (
                                <div>
                                  <p className="text-slate-700"><strong>Special Instructions:</strong></p>
                                  <p className="text-slate-600 italic">{order.special_instructions}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="lg:w-64 flex flex-col space-y-3">
                          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                            <h4 className="font-semibold text-slate-900 mb-3">Actions</h4>
                            
                            {order.status === 'Quote Pending' && (
                              <>
                                <Button
                                  onClick={() => openSendQuoteModal(order)}
                                  disabled={actionLoading === order.id}
                                  className="w-full mb-2 bg-blue-600 hover:bg-blue-700"
                                >
                                  <Mail className="h-4 w-4 mr-2" />
                                  Send Quote with Price
                                </Button>

                                <Button
                                  onClick={() => handleApproveQuote(order.id, order.quote_id)}
                                  disabled={actionLoading === order.id}
                                  className="w-full mb-2 bg-emerald-600 hover:bg-emerald-700"
                                >
                                  {actionLoading === order.id ? (
                                    <>
                                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                      Approving...
                                    </>
                                  ) : (
                                    <>
                                      <Check className="h-4 w-4 mr-2" />
                                      Approve Quote
                                    </>
                                  )}
                                </Button>
                                
                                <Button
                                  onClick={() => handleRejectQuote(order.id)}
                                  disabled={actionLoading === order.id}
                                  variant="outline"
                                  className="w-full border-red-300 text-red-700 hover:bg-red-50"
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Reject Quote
                                </Button>
                              </>
                            )}
                            
                            {order.status === 'Quote Sent' && (
                              <div className="text-sm text-slate-600">
                                <p className="mb-2">✉️ Invoice sent</p>
                                <p className="text-xs">Waiting for customer approval</p>
                              </div>
                            )}
                            
                            {order.status === 'Quote Rejected' && (
                              <>
                                <div className="bg-red-50 p-3 rounded-lg border border-red-200 mb-3">
                                  <p className="text-sm text-red-800 mb-1">❌ Quote Rejected</p>
                                  <p className="text-xs text-red-600">This quote was declined</p>
                                </div>
                                
                                <Button
                                  onClick={() => handleUndoReject(order.id)}
                                  disabled={actionLoading === order.id}
                                  variant="outline"
                                  className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                                >
                                  {actionLoading === order.id ? (
                                    <>
                                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                      Restoring...
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="h-4 w-4 mr-2" />
                                      Undo Rejection
                                    </>
                                  )}
                                </Button>
                              </>
                            )}
                          </div>
                          
                          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                            <p className="text-xs text-blue-800">
                              💡 <strong>Tip:</strong> Approving generates tracking ID and sends confirmation email to customer immediately.
                            </p>
                          </div>
                          
                          {/* Delete Quote Button */}
                          <div className="pt-4 border-t border-slate-200">
                            <Button
                              onClick={() => deleteOrder(order.id, order.quote_id)}
                              disabled={actionLoading === order.id}
                              variant="outline"
                              className="w-full text-sm text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                            >
                              {actionLoading === order.id ? (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Quote
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : activeTab === 'calculator' ? (
          <>
            {/* Price Calculator */}
            <Card className="shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Delivery Price Calculator</h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Calculator Form */}
                  <div className="space-y-6">
                    {/* Distance Input */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Distance (miles) *
                      </label>
                      <Input
                        type="number"
                        value={calcDistance}
                        onChange={(e) => setCalcDistance(e.target.value)}
                        placeholder="Enter distance (0-500 miles)"
                        min="0"
                        max="500"
                        step="0.1"
                        className="w-full"
                      />
                      <p className="text-xs text-slate-500 mt-1">Maximum 500 miles</p>
                    </div>

                    {/* Vehicle Type */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Vehicle Type *
                      </label>
                      <select
                        value={calcVehicle}
                        onChange={(e) => setCalcVehicle(e.target.value)}
                        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="Small Van">Small Van</option>
                        <option value="SWB">SWB (Short Wheelbase)</option>
                        <option value="MWB">MWB (Medium Wheelbase)</option>
                        <option value="XLWB">XLWB (Extra Long Wheelbase)</option>
                        <option value="Luton">Luton Van</option>
                      </select>
                    </div>

                    {/* Delivery Type */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Delivery Type *
                      </label>
                      <select
                        value={calcDeliveryType}
                        onChange={(e) => setCalcDeliveryType(e.target.value)}
                        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="same-day">Same-day/Next-day (1h-12h)</option>
                        <option value="urgent">Urgent Delivery (0h-6h)</option>
                      </select>
                    </div>

                    {/* Manual Price Input (for 0-60 miles) */}
                    {calcDistance && parseFloat(calcDistance) <= 60 && parseFloat(calcDistance) > 0 && (
                      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Enter Price (£) *
                        </label>
                        <Input
                          type="number"
                          value={calcManualPrice}
                          onChange={(e) => setCalcManualPrice(e.target.value)}
                          placeholder={`Range: £${pricingData[calcDeliveryType][calcVehicle].range}`}
                          min="0"
                          step="0.01"
                          className="w-full"
                        />
                        <p className="text-xs text-amber-700 mt-2">
                          For distances 0-60 miles, price range is <strong>£{pricingData[calcDeliveryType][calcVehicle].range}</strong>. Please enter a price within this range.
                        </p>
                      </div>
                    )}

                    {/* Buttons */}
                    <div className="flex space-x-3">
                      <Button
                        onClick={calculatePrice}
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                      >
                        Calculate Price
                      </Button>
                      <Button
                        onClick={resetCalculator}
                        variant="outline"
                        className="hover:bg-slate-100"
                      >
                        Reset
                      </Button>
                    </div>
                  </div>

                  {/* Result Display */}
                  <div>
                    {calculatedPrice ? (
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg border-2 border-purple-300">
                        <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center">
                          <CheckCircle className="h-5 w-5 mr-2" />
                          Price Calculated
                        </h3>
                        
                        <div className="space-y-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-700">Distance:</span>
                            <span className="font-semibold">{calculatedPrice.distance} miles</span>
                          </div>
                          
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-700">Vehicle:</span>
                            <span className="font-semibold">{calculatedPrice.vehicle}</span>
                          </div>
                          
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-700">Delivery Type:</span>
                            <span className="font-semibold">{calculatedPrice.deliveryType}</span>
                          </div>
                          
                          {calculatedPrice.priceRange && (
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-700">Price Range:</span>
                              <span className="font-semibold">£{calculatedPrice.priceRange}</span>
                            </div>
                          )}
                          
                          {calculatedPrice.perMileRate && (
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-700">Rate per Mile:</span>
                              <span className="font-semibold">£{calculatedPrice.perMileRate}/mile</span>
                            </div>
                          )}
                          
                          <div className="pt-3 border-t-2 border-purple-300">
                            <div className="flex justify-between items-center">
                              <span className="text-lg font-bold text-slate-900">Total Price:</span>
                              <div className="text-right">
                                <span className="text-3xl font-bold text-purple-900">£{calculatedPrice.finalPrice.toFixed(2)}</span>
                                <span className="text-sm font-normal text-slate-600 ml-2">+ VAT</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-white/60 p-3 rounded text-xs text-slate-600 mt-4">
                            <strong>Calculation:</strong> {calculatedPrice.calculation}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-slate-50 p-6 rounded-lg border-2 border-dashed border-slate-300 h-full flex items-center justify-center">
                        <div className="text-center">
                          <Package className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                          <p className="text-slate-600 font-medium">Enter details to calculate price</p>
                          <p className="text-xs text-slate-500 mt-2">Fill in the form and click Calculate</p>
                        </div>
                      </div>
                    )}

                    {/* Pricing Guide */}
                    <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h4 className="text-sm font-bold text-blue-900 mb-3">📋 Pricing Guide</h4>
                      <div className="space-y-2 text-xs text-blue-800">
                        <p><strong>0-60 miles:</strong> Fixed price range (admin selects within range)</p>
                        <p><strong>60-500 miles:</strong> Calculated automatically using per-mile rate</p>
                        <p><strong>Urgent delivery:</strong> Higher rates for 0-6 hour delivery</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : activeTab === 'invoice' ? (
          <>
            {/* Invoice Generator */}
            <InvoiceGenerator />
          </>
        ) : activeTab === 'blog' ? (
          <>
            {/* Blog Manager */}
            <BlogManager />
          </>
        ) : (
          <>
            {/* Orders List */}
            {loading ? (
              <Card className="shadow-lg">
                <CardContent className="p-12 text-center">
                  <RefreshCw className="h-8 w-8 text-emerald-600 animate-spin mx-auto mb-4" />
                  <p className="text-slate-600">Loading orders...</p>
                </CardContent>
              </Card>
            ) : filteredOrders.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="p-12 text-center">
                  <Package className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Orders Found</h3>
                  <p className="text-slate-600">
                    {orderFilter === 'all' 
                      ? 'No orders have been placed yet.' 
                      : `No ${orderFilter} orders found.`
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map((order) => (
                  <Card key={order.id} className="shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row justify-between space-y-4 lg:space-y-0 lg:space-x-6">
                        {/* Order Info */}
                        <div className="flex-1">
                          {/* Quote Reference Section - Prominent Display */}
                          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0">
                              <div className="flex-1">
                                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Quote Reference</p>
                                <h3 className="text-2xl font-bold text-blue-900 font-mono tracking-wider">
                                  {order.quote_id}
                                </h3>
                              </div>
                              {order.tracking_id && (
                                <div className="flex-1 sm:border-l-2 sm:border-blue-300 sm:pl-4">
                                  <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">Tracking ID</p>
                                  <h3 className="text-2xl font-bold text-emerald-900 font-mono tracking-wider">
                                    {order.tracking_id}
                                  </h3>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row justify-between items-start mb-4">
                            <div>
                              <div className="flex items-center space-x-2 mb-1">
                                <User className="h-4 w-4 text-slate-500" />
                                <span className="text-lg font-semibold text-slate-900">{order.customer_name}</span>
                                {order.company && (
                                  <span className="text-slate-500">• {order.company}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Badge className={getOrderStatusColor(order.status)}>
                                {order.status}
                              </Badge>
                              {order.urgent && (
                                <Badge className="bg-red-100 text-red-800">
                                  ⚡ URGENT
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="font-medium text-slate-900">Contact:</p>
                              <p className="text-slate-600">{order.phone}</p>
                              <p className="text-slate-600">{order.email}</p>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">Pickup:</p>
                              <p className="text-slate-600">{order.pickup_address}</p>
                              <p className="text-slate-600">{order.pickup_date} at {order.pickup_time}</p>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">Delivery:</p>
                              <p className="text-slate-600">{order.delivery_address}</p>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">Vehicle:</p>
                              <p className="text-slate-600">{order.vehicle_type}</p>
                              {order.driver_name && (
                                <p className="text-slate-600">Driver: {order.driver_name}</p>
                              )}
                            </div>
                          </div>
                          
                          {order.delivery_photos.length > 0 && (
                            <div className="mt-4">
                              <p className="font-medium text-slate-900 mb-2">Delivery Photos ({order.delivery_photos.length}):</p>
                              <div className="flex flex-wrap gap-2">
                                {order.delivery_photos.map((photo, index) => (
                                  <div key={index} className="relative group">
                                    <img
                                      src={photo}
                                      alt={`Delivery photo ${index + 1}`}
                                      className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80 border"
                                      onClick={() => {
                                        // Open in new window for better viewing
                                        const newWindow = window.open();
                                        newWindow.document.write(`
                                          <html>
                                            <head><title>Delivery Photo ${index + 1}</title></head>
                                            <body style="margin:0; background:#000; display:flex; align-items:center; justify-content:center;">
                                              <img src="${photo}" style="max-width:100%; max-height:100vh; object-fit:contain;" />
                                            </body>
                                          </html>
                                        `);
                                      }}
                                    />
                                    <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                      ×
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Progress Steps & Actions */}
                        <div className="flex flex-col space-y-4 lg:w-80">
                          {/* Progress Steps */}
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-slate-700 mb-2 block">Order Progress</label>
                            <div className="space-y-2">
                              {getOrderProgressSteps(order).map((step, stepIndex) => (
                                <div 
                                  key={stepIndex}
                                  className={`flex items-center justify-between p-2 rounded-lg border text-xs ${getStepColor(step)}`}
                                >
                                  <span className="font-medium">{step.name}</span>
                                  <div className="flex items-center space-x-1">
                                    {step.completed && (
                                      <CheckCircle className="h-4 w-4 text-green-600" />
                                    )}
                                    {step.next && (
                                      <span className="text-orange-600 font-bold">NEXT</span>
                                    )}
                                    {step.current && !step.completed && (
                                      <Clock className="h-4 w-4" />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Quick Action Buttons */}
                          <div className="space-y-2">
                            <label className="text-xs font-medium text-slate-700">Quick Actions</label>
                            {order.status !== 'Delivered' && (
                              <select
                                data-order-id={order.id}
                                onChange={(e) => {
                                  if (e.target.value !== '') {
                                    handleStatusChange(order.id, e.target.value, order.status);
                                    e.target.value = ''; // Reset dropdown
                                  }
                                }}
                                value=""
                                disabled={actionLoading === order.id}
                                className="w-full p-2 border rounded-md text-sm bg-blue-50 border-blue-200"
                              >
                                <option value="">Update Status...</option>
                                <option value="Order Received">Mark as Order Received</option>
                                <option value="Booking Confirmed">Mark as Booking Confirmed</option>
                                <option value="Driver Assigned">Mark as Driver Assigned</option>
                                <option value="Package Collected">Mark as Package Collected</option>
                                <option value="In Transit">Mark as In Transit</option>
                                <option value="Out for Delivery">Mark as Out for Delivery</option>
                                <option value="Delivered">Mark as Delivered</option>
                              </select>
                            )}
                          </div>
                          
                          {order.status !== 'Delivered' && (
                            <Button
                              onClick={() => {
                                const driverName = prompt('Driver Name:');
                                if (driverName) {
                                  const driverPhone = prompt('Driver Phone:');
                                  const driverVehicle = prompt('Driver Vehicle:');
                                  if (window.confirm(`Assign driver "${driverName}" to this order?`)) {
                                    updateOrderStatus(order.id, 'Driver Assigned', {
                                      driver_name: driverName,
                                      driver_phone: driverPhone || '',
                                      driver_vehicle: driverVehicle || ''
                                    });
                                  }
                                }
                              }}
                              disabled={actionLoading === order.id}
                              variant="outline"
                              className="w-full text-sm"
                            >
                              Assign Driver
                            </Button>
                          )}
                          
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-slate-700">Upload Photo</label>
                            <div className="relative">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handlePhotoUpload(order.id, e)}
                                disabled={actionLoading === `photo_${order.id}`}
                                className="w-full text-sm text-slate-500 file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 file:cursor-pointer"
                              />
                              {actionLoading === `photo_${order.id}` && (
                                <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md">
                                  <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-xs text-slate-500 mt-2">
                            <p>Created: {order.created_at}</p>
                            {order.delivery_photos.length > 0 && (
                              <p>Photos: {order.delivery_photos.length}</p>
                            )}
                          </div>
                          
                          {/* Delete Order Button */}
                          <div className="pt-4 border-t border-slate-200">
                            <Button
                              onClick={() => deleteOrder(order.id, order.tracking_id || order.quote_id)}
                              disabled={actionLoading === order.id}
                              variant="outline"
                              className="w-full text-sm text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                            >
                              {actionLoading === order.id ? (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Order
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Send Quote Modal */}
      {sendQuoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Send Quote to Customer</h2>
                <Button
                  onClick={closeSendQuoteModal}
                  variant="outline"
                  size="sm"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Customer Details */}
              <div className="bg-slate-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold text-slate-900 mb-2">Customer Information</h3>
                <div className="text-sm space-y-1">
                  <p><strong>Name:</strong> {sendQuoteModal.name}</p>
                  <p><strong>Email:</strong> {sendQuoteModal.email}</p>
                  <p><strong>Phone:</strong> {sendQuoteModal.phone}</p>
                  <p><strong>Pickup:</strong> {sendQuoteModal.pickup_address}</p>
                  <p><strong>Delivery:</strong> {sendQuoteModal.delivery_address}</p>
                  <p><strong>Vehicle:</strong> {sendQuoteModal.vehicle_type}</p>
                </div>
              </div>

              {/* Price Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Quote Price (£) *
                </label>
                <Input
                  type="number"
                  value={quotePrice}
                  onChange={(e) => setQuotePrice(e.target.value)}
                  placeholder="Enter price (e.g., 145.00)"
                  min="0"
                  step="0.01"
                  className="text-lg"
                />
                <p className="text-xs text-slate-500 mt-1">Enter the base price (VAT will be added automatically)</p>
                
                {/* VAT Calculation Display */}
                {quotePrice && parseFloat(quotePrice) > 0 && (
                  <div className="mt-4 bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Subtotal:</span>
                        <span className="font-medium">£{parseFloat(quotePrice).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">VAT (20%):</span>
                        <span className="font-medium">£{(parseFloat(quotePrice) * 0.20).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-slate-300">
                        <span className="font-bold text-slate-900">Total:</span>
                        <span className="font-bold text-emerald-600 text-lg">£{(parseFloat(quotePrice) * 1.20).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Message to Customer *
                </label>
                <textarea
                  value={quoteMessage}
                  onChange={(e) => setQuoteMessage(e.target.value)}
                  placeholder="Enter your message to the customer..."
                  rows="5"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">This message will be sent with the quote</p>
              </div>

              {/* Preview */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Quote Summary</h3>
                <div className="text-sm text-blue-800">
                  <p><strong>Customer will receive:</strong></p>
                  <ul className="list-disc list-inside ml-2 mt-2 space-y-1">
                    <li>Subtotal: £{quotePrice || '0.00'}</li>
                    <li>VAT (20%): £{quotePrice ? (parseFloat(quotePrice) * 0.20).toFixed(2) : '0.00'}</li>
                    <li>Total price: £{quotePrice ? (parseFloat(quotePrice) * 1.20).toFixed(2) : '0.00'}</li>
                    <li>Your personalized message</li>
                    <li>Delivery details (pickup, delivery, vehicle type)</li>
                    <li>Your contact information to confirm</li>
                  </ul>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={sendQuoteToCustomer}
                  disabled={actionLoading === sendQuoteModal.id}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {actionLoading === sendQuoteModal.id ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Quote to Customer
                    </>
                  )}
                </Button>
                <Button
                  onClick={closeSendQuoteModal}
                  variant="outline"
                  className="w-32"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};